import { IBannerData } from "@faire/retailer/lib/bannerUtils";
import { ISearchCatNavBannersRequest } from "@faire/web-api/indigofair/data/ISearchCatNavBannersRequest";
import { ISearchCatNavBannersResponse } from "@faire/web-api/indigofair/data/ISearchCatNavBannersResponse";
import { WebApiRequestCancellationError } from "@faire/web-api/WebApiRequestCancellationError";
import { logError } from "@faire/web/common/logging";
import { makeObservable } from "@faire/web/common/makeObservable";
import cloneDeep from "lodash/cloneDeep";
import { action, computed, observable } from "mobx";

import { CachedBannerResponseStore } from "./CachedBannerResponseStore";

const SPACE_BETWEEN_BANNERS_MOBILE = 3;
const SPACE_BETWEEN_BANNERS_TABLET_AND_ABOVE = 4;

export abstract class BannersViewState<T> {
  // First item should be highest priority, last item should be lowest priority
  abstract get eligibleBannersDisplayDataInPriorityOrder(): IBannerData<T>[];

  @observable
  protected bannersResponse?: ISearchCatNavBannersResponse;

  @observable
  protected pageNumber: number = 0;

  constructor() {
    makeObservable(this);
  }

  @action
  fetchBanners = async (
    request: ISearchCatNavBannersRequest,
    abortSignal?: AbortSignal
  ) => {
    try {
      const response = await CachedBannerResponseStore.get().cachedRequest(
        request,
        {
          signal: abortSignal,
        }
      );
      this.bannersResponse = response;
    } catch (error) {
      if (!(error instanceof WebApiRequestCancellationError)) {
        logError(error);
      }
    }
  };

  @action
  setBanners = (banners: ISearchCatNavBannersResponse) => {
    this.bannersResponse = banners;
  };

  @computed
  get replacedProductTilesCount() {
    return this.eligibleBannersDisplayDataInPriorityOrder.reduce(
      (prev, current) => {
        if (current.useStandardPageSize) {
          return prev;
        }
        return prev + current.size;
      },
      0
    );
  }

  private getSpaceBetweenBanners = (isMobile: boolean): number => {
    // TODO(jocelyn): I think this is backwards, but I'm keeping the existing behaviour.
    if (isMobile) {
      return SPACE_BETWEEN_BANNERS_TABLET_AND_ABOVE;
    } else {
      return SPACE_BETWEEN_BANNERS_MOBILE;
    }
  };

  getEligibleBannersDisplayDataWithAssignedRow = (
    isMobile: boolean
  ): IBannerData<T>[] => {
    let bannerRowIndex = 0;
    return this.eligibleBannersDisplayDataInPriorityOrder.map(
      (bannerWithoutRow) => {
        if (bannerWithoutRow.renderAsModal) {
          return {
            ...bannerWithoutRow,
            row: -1,
          };
        }
        bannerRowIndex += this.getSpaceBetweenBanners(isMobile);
        return {
          ...bannerWithoutRow,
          row: bannerRowIndex,
        };
      }
    );
  };

  @computed
  get sortedEligibleBannersDisplayData() {
    return this.eligibleBannersDisplayDataInPriorityOrder.sort(
      (displayDataA, displayDataB) =>
        displayDataA.row - displayDataB.row ||
        displayDataA.column - displayDataB.column
    );
  }

  private displayableBanners = (
    isMobile: boolean,
    itemsPerRow: number,
    itemLength: number,
    displayAsLastItem?: boolean,
    useDynamicRow: boolean = true //[bill.cui@faire.com] if set to true, hardcoded row values will be ignored, the row values will be dynamically generated instead
  ): IBannerData<T>[] => {
    let numReplacedProductTiles = 0;
    // exclude logged out category from banner rework xp
    return this.getBanners(useDynamicRow, isMobile).filter((displayData) => {
      const shouldRender = this.shouldRenderBanner(
        displayData,
        itemsPerRow,
        itemLength,
        displayAsLastItem,
        numReplacedProductTiles
      );
      if (shouldRender && !displayData.fullRowBanner) {
        numReplacedProductTiles += displayData.size;
      }
      return shouldRender;
    });
  };

  displayableBannersWithAdjustedRow = (
    isMobile: boolean,
    itemsPerRow: number,
    itemLength: number,
    displayAsLastItem?: boolean,
    useDynamicRow: boolean = true
  ): IBannerData<T>[] => {
    const banners = cloneDeep(
      this.displayableBanners(
        isMobile,
        itemsPerRow,
        itemLength,
        displayAsLastItem,
        useDynamicRow
      )
    );

    let numFullRowBannersAbove = 0;
    banners.forEach((banner) => {
      if (banner.rowNumberRelativeToProductRow) {
        banner.row += numFullRowBannersAbove;
      }

      if (banner.fullRowBanner) {
        numFullRowBannersAbove += 1;
        if (itemsPerRow === 5) {
          banner.size = 5;
        }
      } else {
        // This will fix banner location when the itemPerRow increased to 5
        // in pages with different itemPerRow
        if (banner.column + banner.size - 1 !== itemsPerRow) {
          banner.column = itemsPerRow - banner.size + 1;
        }
      }
    });

    return banners;
  };

  getBanners = (useDynamicRow: boolean, isMobile: boolean) =>
    cloneDeep(
      useDynamicRow
        ? this.getEligibleBannersDisplayDataWithAssignedRow(isMobile)
        : this.sortedEligibleBannersDisplayData
    );

  displayableBannersWithManuallyOffsetRow = (
    isMobile: boolean,
    itemsPerRow: number,
    itemLength: number,
    offset: number,
    displayAsLastItem?: boolean,
    useDynamicRow: boolean = true
  ): IBannerData<T>[] => {
    let numFullRowBannersAbove = 0;
    const banners = this.getBanners(useDynamicRow, isMobile);
    banners.forEach((banner) => {
      banner.row += offset;

      if (banner.rowNumberRelativeToProductRow) {
        banner.row += numFullRowBannersAbove;
      }

      if (banner.fullRowBanner) {
        numFullRowBannersAbove += 1;
        if (itemsPerRow === 5) {
          banner.size = 5;
        }
      } else {
        // This will fix banner location when the itemPerRow increased to 5
        // in pages with different itemPerRow
        if (banner.column + banner.size - 1 !== itemsPerRow) {
          banner.column = itemsPerRow - banner.size + 1;
        }
      }
    });

    let numReplacedProductTiles = 0;
    const displayableBanners = banners.filter((displayData) => {
      if (displayData.row <= 0) {
        return false;
      }
      const shouldRender = this.shouldRenderBanner(
        displayData,
        itemsPerRow,
        itemLength,
        displayAsLastItem,
        numReplacedProductTiles
      );
      if (shouldRender && !displayData.fullRowBanner) {
        numReplacedProductTiles += displayData.size;
      }
      return shouldRender;
    });

    return displayableBanners;
  };

  shouldRenderBanner = (
    banner: IBannerData<T>,
    itemsPerRow: number,
    itemLength: number,
    displayAsLastItem?: boolean,
    numReplacedProductTiles: number = 0
  ): boolean => {
    if (banner.renderAsModal) {
      return true;
    }
    const bannerPosition =
      itemsPerRow * banner.row - banner.size - numReplacedProductTiles;

    if (displayAsLastItem) {
      return itemLength >= bannerPosition;
    }
    return itemLength > bannerPosition;
  };

  @computed
  get getBannersResponse() {
    return this.bannersResponse;
  }

  @computed
  get currentPageNumber() {
    return this.pageNumber;
  }

  setPageNumber = (pageNumber: number) => {
    this.pageNumber = pageNumber;
  };
}
